/**
 * Generic hook for handling notifications with automatic React Query cache invalidation.
 *
 * This provides a simpler API for modules to subscribe to notifications
 * and invalidate caches without boilerplate.
 *
 * Usage:
 *   import { useNotificationHandler } from '@/shared/notifications';
 *
 *   function MyComponent() {
 *     useNotificationHandler({
 *       'item.assigned': {
 *         invalidate: ['items'],
 *         // Optional: custom handler in addition to cache invalidation
 *         onNotification: (notification) => {
 *           console.log('Item assigned:', notification.entity_id);
 *         },
 *       },
 *       'item.comment': {
 *         invalidate: (notification) => [
 *           ['items', notification.entity_id],
 *           ['items', notification.entity_id, 'comments'],
 *         ],
 *       },
 *     });
 *   }
 */

import { useEffect } from 'react';
import { useQueryClient, QueryKey } from '@tanstack/react-query';
import { useRealtimeNotifications } from './NotificationContext';
import type { Notification, NotificationType } from './types';

/**
 * Handler configuration for a notification type.
 */
interface NotificationHandlerConfig {
  /**
   * Query keys to invalidate when this notification is received.
   * Can be:
   * - A single query key array: ['items']
   * - Multiple query keys: [['items'], ['boards']]
   * - A function that receives the notification and returns keys
   */
  invalidate?:
    | QueryKey
    | QueryKey[]
    | ((notification: Notification) => QueryKey | QueryKey[]);

  /**
   * Optional callback to run when notification is received.
   * Called after cache invalidation.
   */
  onNotification?: (notification: Notification) => void;

  /**
   * If true, only invalidate if the notification's entity_id matches
   * a specific value. Useful for detail views.
   */
  entityId?: string;
}

/**
 * Map of notification types to their handler configurations.
 */
export type NotificationHandlerMap = {
  [K in NotificationType | '*']?: NotificationHandlerConfig;
};

/**
 * Hook for handling notifications with automatic cache invalidation.
 *
 * @param handlers - Map of notification types to handler configurations
 */
export function useNotificationHandler(handlers: NotificationHandlerMap): void {
  const { subscribe } = useRealtimeNotifications();
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    for (const [type, config] of Object.entries(handlers)) {
      if (!config) continue;

      const unsubscribe = subscribe(type as NotificationType | '*', (notification) => {
        // Check entity ID filter if specified
        if (config.entityId && notification.entity_id !== config.entityId) {
          return;
        }

        // Handle cache invalidation
        if (config.invalidate) {
          const keys = typeof config.invalidate === 'function'
            ? config.invalidate(notification)
            : config.invalidate;

          // Normalize to array of keys
          const keyArray = Array.isArray(keys[0]) ? keys as QueryKey[] : [keys as QueryKey];

          for (const key of keyArray) {
            queryClient.invalidateQueries({ queryKey: key });
          }
        }

        // Call custom handler if provided
        if (config.onNotification) {
          try {
            config.onNotification(notification);
          } catch (err) {
            console.error('Notification handler error:', err);
          }
        }
      });

      unsubscribers.push(unsubscribe);
    }

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [subscribe, queryClient, handlers]);
}

/**
 * Simpler hook for just invalidating queries on notifications.
 *
 * Usage:
 *   useNotificationInvalidation('item.assigned', ['items']);
 *   useNotificationInvalidation('message.new', (n) => ['messages', n.data.conversation_id]);
 */
export function useNotificationInvalidation(
  type: NotificationType,
  queryKeys: QueryKey | ((notification: Notification) => QueryKey),
): void {
  useNotificationHandler({
    [type]: {
      invalidate: queryKeys,
    },
  });
}
